import React, { createContext, useContext, useState, useEffect } from 'react';

// Auto-cierre por inactividad: si no hay actividad del usuario durante este
// tiempo, se cierra la sesión y se vuelve al login (seguridad de caja).
const INACTIVIDAD_MS = 60 * 60 * 1000; // 1 hora
import { loginApi, registerContadorApi } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('erp_user');
    return saved ? JSON.parse(saved) : null;
  });
  // Contador: empresa activa que está revisando { id, nombre }.
  const [actingTenant, setActingTenantState] = useState(() => {
    const saved = localStorage.getItem('erp_acting_tenant');
    const nombre = localStorage.getItem('erp_acting_nombre');
    return saved ? { id: saved, nombre: nombre || '' } : null;
  });

  const guardarSesion = (data) => {
    localStorage.setItem('erp_token', data.token);
    localStorage.setItem('erp_user', JSON.stringify(data.user));
    setUser(data.user);
  };

  const login = async (email, password) => {
    const res = await loginApi({ email, password });
    guardarSesion(res.data);
    return res.data;
  };

  // Inicia sesión con una respuesta ya emitida (p. ej. SSO desde la Consola).
  const loginConSesion = (data) => { guardarSesion(data); return data; };

  const registerContador = async (data) => {
    const res = await registerContadorApi(data);
    guardarSesion(res.data);
    return res.data;
  };

  const setActingTenant = (id, nombre) => {
    if (id) {
      localStorage.setItem('erp_acting_tenant', id);
      localStorage.setItem('erp_acting_nombre', nombre || '');
      setActingTenantState({ id, nombre: nombre || '' });
    } else {
      localStorage.removeItem('erp_acting_tenant');
      localStorage.removeItem('erp_acting_nombre');
      setActingTenantState(null);
    }
  };

  const logout = () => {
    localStorage.removeItem('erp_token');
    localStorage.removeItem('erp_user');
    localStorage.removeItem('erp_acting_tenant');
    localStorage.removeItem('erp_acting_nombre');
    localStorage.removeItem('erp_last_activity');
    setUser(null);
    setActingTenantState(null);
  };

  // Auto-logout por inactividad (solo con sesión activa). Reinicia el contador
  // con cualquier actividad; si pasa 1 hora sin actividad, cierra y va a login.
  useEffect(() => {
    if (!user) return;
    let ultima = Number(localStorage.getItem('erp_last_activity')) || Date.now();
    const marcar = () => {
      const n = Date.now();
      if (n - ultima > 2000) { ultima = n; try { localStorage.setItem('erp_last_activity', String(n)); } catch (_) {} }
    };
    const revisar = () => {
      if (Date.now() - ultima > INACTIVIDAD_MS) {
        logout();
        if (window.location.pathname !== '/login') window.location.href = '/login';
      }
    };
    const eventos = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    eventos.forEach(e => window.addEventListener(e, marcar, { passive: true }));
    const onVis = () => { if (document.visibilityState === 'visible') revisar(); };
    document.addEventListener('visibilitychange', onVis);
    const intervalo = setInterval(revisar, 30 * 1000);
    marcar();
    return () => {
      eventos.forEach(e => window.removeEventListener(e, marcar));
      document.removeEventListener('visibilitychange', onVis);
      clearInterval(intervalo);
    };
  }, [user]); // eslint-disable-line

  return (
    <AuthContext.Provider value={{ user, login, loginConSesion, registerContador, logout, actingTenant, setActingTenant }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
